import moment from 'moment';
import { integerDeclination, killNulls } from 'utils';
import {
  HouseChatPreviewIcon,
  HouseChatsStatusFilter,
  IHouseChatPreview,
} from './types';
import {
  ICON_BY_ATTACHMENT_TYPE,
  IGNORED_ATTACHMENT_TYPES,
  MAX_ATTACHMENT_ICONS,
  NO_MESSAGE_PREVIEW,
} from './constants';
import { Files } from 'http-requests';
import {
  ChatConnectionStatus,
  HistoryMessageDTO,
  MassiveMessageItemDTO,
} from 'http-requests/house-chats';

const ATTACHMENT_SHARE_DURATION_MINUTES = 15;

// единственный критерий "своего" сообщения в домовых чатах — наличие
// system_sender (сообщение отправлено сотрудником из этой панели)
export const isOwnSender = (system_sender: unknown): boolean => !!system_sender;

export const dropIgnoredAttachments = <T extends { type?: string | null }>(
  attachments: T[]
): T[] =>
  attachments.filter(
    (attachment) => !IGNORED_ATTACHMENT_TYPES.includes(attachment.type ?? '')
  );

// username у system_sender — номер сотрудника (user.auth.number). Сообщение
// коллеги из панели должно считаться непрочитанным, собственное — нет.
export const isCurrentUserSender = (
  system_sender: { username?: string | null } | null | undefined,
  currentUserNumber: string
): boolean =>
  !!currentUserNumber && system_sender?.username === currentUserNumber;

const STATUS_FILTER_MAP: Record<
  HouseChatsStatusFilter,
  ChatConnectionStatus[] | undefined
> = {
  all: undefined,
  connected: ['connected'],
  disconnected: ['bot_not_added', 'bot_not_admin'],
};

export const buildChatsFilterParams = (filters: {
  statusFilter: HouseChatsStatusFilter;
  unreadOnly: boolean;
}): {
  status?: ChatConnectionStatus[];
  unread_only?: boolean;
} =>
  killNulls({
    status: STATUS_FILTER_MAP[filters.statusFilter],
    unread_only: filters.unreadOnly || undefined,
  });

export const uploadAttachmentFile = async (
  file: File
): Promise<{ file_id: string; filename: string }> => {
  const { file_id, shared_id } = await Files.ReserveFile({
    importance: 'low',
    filename: file.name,
    shared_until: moment()
      .add(ATTACHMENT_SHARE_DURATION_MINUTES, 'minutes')
      .toISOString(),
    file_size: file.size,
  });

  const formData = new FormData();
  formData.append('file', file);
  formData.append('importance', 'low');

  await Files.SaveFileToFilesMS(shared_id, formData);

  return { file_id, filename: file.name };
};

// У массовых рассылок нет is_edited/is_deleted/reply_* — приводим к форме
// HistoryMessageDTO, чтобы переиспользовать ChatHistoryModel (пагинация/
// оптимистичные сообщения) как для обычной истории чата.
// reply_preview у массовых сообщений не бывает — кладём пустую заглушку
// (ChatMessageModel/MassMailingMessage её не читают). sender у
// HistoryMessageDTO объявлен не-nullable строже, чем есть на самом деле
// (ChatMessageModel и так допускает null) — cast локален, наружу не течёт.
// max_message_id берём из chats[0] — GetAttachment резолвится по конкретному
// чату и id сообщения именно в нём, massive_id для этого не годится.
export const normalizeMassiveMessage = (
  item: MassiveMessageItemDTO
): HistoryMessageDTO => {
  const firstMessageId = Object.values(item.chats)[0];

  return {
    id: item.massive_id,
    max_message_id: firstMessageId ?? item.massive_id,
    text: item.text,
    created_at: item.created_at,
    sender: item.sender,
    system_sender: item.system_sender,
    has_attachments: item.has_attachments,
    attachments: item.attachments,
    is_edited: false,
    is_deleted: false,
    is_massive: true,
    is_repost: false,
    repost_sender: null,
    repost_source_chat_id: null,
    chats: item.chats,
    deleted_by: null,
    reply_to_max_id: null,
    reply_preview: { sender_name: '', preview: '', is_deleted: false },
  } as HistoryMessageDTO;
};

// Пока бот не добавлен в чат, бэк не знает chat_id (и присылает null) — но
// houses у такой записи уже известны. Ключуем по ним, отсортированными и
// склеенными, чтобы при повторных /chats запись не плодила новую строку в
// сайдбаре и не сбрасывала выбор — набор домов у одной и той же заявки на
// подключение между фетчами не меняется.
export const chatMapKey = (chat: {
  chat_id: number | null;
  house_ids: string[];
}): string =>
  chat.chat_id !== null
    ? String(chat.chat_id)
    : `pending-${[...chat.house_ids].sort().join(',')}`;

// 'bot_not_added' и "чистый" bot_not_admin (без истории) не годятся для
// массовой рассылки — туда физически некому доставить сообщение.
export const isChatConnected = (chat: {
  status: ChatConnectionStatus;
  last_message: unknown;
}): boolean =>
  chat.status === 'connected' ||
  chat.status === 'disconnected' ||
  (chat.status === 'bot_not_admin' && !!chat.last_message);

export const parseChatId = (rowId: string | null): number | null => {
  if (!rowId?.startsWith('chat-')) {
    return null;
  }

  const chatId = Number(rowId.slice('chat-'.length));

  return Number.isNaN(chatId) ? null : chatId;
};

const ATTACHMENT_LABEL: Record<
  Exclude<HouseChatPreviewIcon, 'repost' | 'forward' | 'unsupported'>,
  string
> = {
  image: 'Фото',
  video: 'Видео',
  audio: 'Аудио',
  file: 'Файл',
};

export const formatAttachmentsPreview = (attachmentTypes: string[]): string => {
  const count = attachmentTypes.length;
  const categories = new Set(
    attachmentTypes.map(
      (type) => ICON_BY_ATTACHMENT_TYPE[type.toLowerCase()] ?? 'file'
    )
  );

  if (categories.size > 1) {
    return `${count} ${integerDeclination(count, ['вложение', 'вложения', 'вложений'])}`;
  }

  const category = categories.values().next()
    .value as keyof typeof ATTACHMENT_LABEL;
  const label = ATTACHMENT_LABEL[category];

  if (count === 1) {
    return label;
  }

  return category === 'file'
    ? `${count} ${integerDeclination(count, ['файл', 'файла', 'файлов'])}`
    : `${count} ${label.toLowerCase()}`;
};

export const formatParticipantsCount = (count: number): string =>
  `${count} ${integerDeclination(count, ['участник', 'участника', 'участников'])}`;

export const getAttachmentIcons = (types: string[]): HouseChatPreviewIcon[] => {
  const icons: HouseChatPreviewIcon[] = [];

  for (const type of types) {
    const icon = ICON_BY_ATTACHMENT_TYPE[type.toLowerCase()] ?? 'file';

    if (!icons.includes(icon)) {
      icons.push(icon);
    }

    if (icons.length >= MAX_ATTACHMENT_ICONS) {
      break;
    }
  }

  return icons;
};

export const buildSenderName = (
  sender: {
    first_name: string | null;
    last_name: string | null;
  } | null
): string | null => {
  if (!sender) {
    return null;
  }

  const name = [sender.first_name, sender.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  return name || null;
};

interface IPreviewSource {
  text: string | null;
  is_deleted: boolean;
  max_message_id: string;
  is_repost: boolean;
  system_sender: unknown | null;
  attachments: { id: string; type: string }[];
}

const pickThumbnails = (
  message: IPreviewSource
): IHouseChatPreview['thumbnails'] =>
  message.attachments
    .filter((item) => {
      const icon = ICON_BY_ATTACHMENT_TYPE[item.type.toLowerCase()];

      return icon === 'image' || icon === 'video';
    })
    .map((attachment) => ({
      id: attachment.id,
      messageId: message.max_message_id,
      type: attachment.type,
    }));

export const buildMessagePreview = (
  message: IPreviewSource | null
): IHouseChatPreview => {
  if (!message) {
    return NO_MESSAGE_PREVIEW;
  }

  if (message.is_deleted) {
    return {
      text: 'Сообщение удалено',
      icons: [],
      isMuted: true,
      thumbnails: [],
    };
  }

  const icons: HouseChatPreviewIcon[] = [];

  if (isOwnSender(message.system_sender)) {
    icons.push('forward');
  }

  if (message.is_repost) {
    icons.push('repost');
  }

  const caption = message.text?.trim();
  const attachmentTypes = dropIgnoredAttachments([
    ...message.attachments,
  ]).map((attachment) => attachment.type);

  if (!caption && !attachmentTypes.length) {
    return {
      text: 'Формат не поддерживается',
      icons: [...icons, 'unsupported'],
      isMuted: true,
      thumbnails: [],
    };
  }

  return {
    text: caption || formatAttachmentsPreview(attachmentTypes),
    icons: [...icons, ...getAttachmentIcons(attachmentTypes)],
    isMuted: false,
    thumbnails: pickThumbnails(message),
  };
};
